/**
 * Created by hzzhao 4/3/2020
 * This file includes the functions which are used accross different domains and not related to specific features
 */

import * as constants from './constants';

/** debounce a function in a specific interval
 * @param  {function} fn - debounce function
 * @param  {integer} delay - debounce interval
 */
export const debounce = (fn, delay) => {
  let timer = null;
  return function (...args) {
    const context = this;
    timer && clearTimeout(timer);
    timer = setTimeout(() => {
      fn.apply(context, args);
    }, delay);
  };
};

/** convert cmyk to rgb
 * @param  {string} cmyk - e.g. '20 30 40 50'
 * @returns {string} rgb - e.g. '#123456'
 */
export const cmykToRGB = function (cmyk) {
  for (let i = 0; i < constants.colors.length; i++) {
    if (cmyk === constants.colors[i][1]) {
      return constants.colors[i][0];
    }
  }

  let aCMYK = cmyk.split(' ');
  const iRange = 255;

  let fnNaNCheck = function (fVal) {
    if (isNaN(fVal)) {
      return 0;
    } else {
      return fVal;
    }
  };

  let iCPrime = fnNaNCheck(aCMYK[0] * 0.01 * (1 - aCMYK[3] * 0.01) + aCMYK[3] * 0.01);
  let iMPrime = fnNaNCheck(aCMYK[1] * 0.01 * (1 - aCMYK[3] * 0.01) + aCMYK[3] * 0.01);
  let IYPrime = fnNaNCheck(aCMYK[2] * 0.01 * (1 - aCMYK[3] * 0.01) + aCMYK[3] * 0.01);

  let sR = Math.round((1 - iCPrime) * iRange).toString(16);
  let sG = Math.round((1 - iMPrime) * iRange).toString(16);
  let sB = Math.round((1 - IYPrime) * iRange).toString(16);

  sR = sR.length === 1 ? '0' + sR : sR;
  sG = sG.length === 1 ? '0' + sG : sG;
  sB = sB.length === 1 ? '0' + sB : sB;

  return `${sR}${sG}${sB}`;
};

/*
 * Converts a RGB String to CMYK
 */
export const rgbToCMYK = (rgb) => {
  const colorWithoutPound = rgb.replace('#', '');
  const colorMatrix = constants.colors.find((c) => c[0].toLowerCase() === rgb.toLowerCase());
  if (colorMatrix) {
    return colorMatrix[1];
  }

  const iRange = 255;
  const sR = colorWithoutPound.slice(0, 2);
  const sG = colorWithoutPound.slice(2, 4);
  const sB = colorWithoutPound.slice(4, 6);
  const fnNaNCheck = (fVal) => (isNaN(fVal) ? 0 : fVal);

  const fnGetMin = (aVal) => {
    let iCurMin;
    for (let i = 0; i <= aVal.length; i++) {
      if (i === 0) {
        iCurMin = aVal[0];
      } else {
        if (aVal[i] < iCurMin) {
          iCurMin = aVal[i];
        }
      }
    }
    return iCurMin;
  };

  const iCPrime = fnNaNCheck(1 - parseInt(sR, 16) / iRange);
  const iMPrime = fnNaNCheck(1 - parseInt(sG, 16) / iRange);
  const iYPrime = fnNaNCheck(1 - parseInt(sB, 16) / iRange);
  const iKPrime = fnGetMin([iCPrime, iMPrime, iYPrime]);

  const iPerC = fnNaNCheck(Math.round(((iCPrime - iKPrime) / (1 - iKPrime)) * 100));
  const iPerM = fnNaNCheck(Math.round(((iMPrime - iKPrime) / (1 - iKPrime)) * 100));
  const iPerY = fnNaNCheck(Math.round(((iYPrime - iKPrime) / (1 - iKPrime)) * 100));
  const iPerK = Math.round(iKPrime * 100);

  return `${iPerC} ${iPerM} ${iPerY} ${iPerK}`;
};

// Merge a `source` object to a `target` recursively
//NOTE: the source can be changed inside the function
export const deepMerge = (target, source) => {
  // Iterate through `source` properties and if an `Object` set property to merge of `target` and `source` properties
  for (const key of Object.keys(source)) {
    if (source[key] instanceof Object)
      source[key] = Object.assign({}, source[key], deepMerge(target[key], source[key]));
  }

  // Join `target` and modified `source`
  return Object.assign({}, target || {}, source);
};

// object deep equal when exclude a key
export const objectDeepEqual = function (obj1, obj2, excludedkey) {
  if (obj1 && typeof obj1 === 'object' && obj2 && typeof obj2 === 'object') {
    if (Array.isArray(obj1) && !Array.isArray(obj2)) {
      return false;
    }
    if (!Array.isArray(obj1) && Array.isArray(obj2)) {
      return false;
    }
    for (let key in obj1) {
      if (key === excludedkey) {
        continue;
      }
      if (obj1.hasOwnProperty(key)) {
        if (!obj2.hasOwnProperty(key)) {
          return false;
        }
      }
    }
    for (let key in obj2) {
      if (key === excludedkey) {
        continue;
      }
      if (obj2.hasOwnProperty(key)) {
        if (!obj1.hasOwnProperty(key)) {
          return false;
        }
      }
    }
    for (let key in obj1) {
      if (key === excludedkey) {
        continue;
      }
      if (obj1.hasOwnProperty(key)) {
        if (!objectDeepEqual(obj1[key], obj2[key], excludedkey)) {
          return false;
        }
      }
    }
    return true;
  } else {
    return obj1 === obj2;
  }
};

/*
 * @description             sort a JSON array by a field
 * @param   array(array)    the JSON array to be sorted
 * @param   field(string)   sort filed
 * @param   reverse(bool)   whether to reverse order
 * @return  array           return a sorted JSON array
 */
export const jsonArraySort = (array, field, reverse) => {
  if (array.length < 2 || !field || typeof array[0] !== 'object') return array;

  //sort by number type
  if (typeof array[0][field] === 'number') {
    array.sort((x, y) => {
      return x[field] - y[field];
    });
  }

  //sort by string type
  if (typeof array[0][field] === 'string') {
    array.sort((x, y) => {
      return x[field].localeCompare(y[field]);
    });
  }

  //reverse order
  if (reverse) {
    array.reverse();
  }

  return array;
};

// validate no special characters and no HTML snippets (< >)
export const validateCharacter = function (data) {
  let message = '';
  let invalidCharacters = '';
  let hasInvalidData = false;
  let validCharacters =
    '1234567890abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ ~!@#$%^&*()_+`-={}[]|\\:";\'?,./';
  let regexCharacters =
    '^[0-9a-zA-Z\\s\\~\\!\\@\\#\\$\\%\\^\\&\\*\\(\\)\\_\\+\\`\\-\\=\\{\\}\\[\\]\\|\\\\\\:\\"\\;\\\'\\?\\,\\.\\/]*$';
  let regex = new RegExp(regexCharacters);
  if (!regex.test(data)) {
    for (let i = 0; i < data.length; i++) {
      if (!validCharacters.includes(data[i])) {
        if (invalidCharacters === '') {
          invalidCharacters += data[i];
        } else {
          invalidCharacters += ',' + data[i];
        }
      }
    }
    hasInvalidData = true;
    message = `You have entered invalid symbols:"${invalidCharacters}", please remove them and try again.`;
    return { hasInvalidData, message };
  }
};

export const calculateFileSize = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

export const hexToRGB = (hex) => {
  hex = hex.replace('#', '').toUpperCase();

  if (hex.length === 3) {
    return hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }

  if (hex.length === 4) {
    return hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }

  if (hex.length === 6) {
    return hex;
  }

  if (hex.length === 8) {
    return hex.slice(0, 6);
  }
};
